/**
 * AdminEstimationPage - Admin dashboard for managing all project estimations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import { Refresh as RefreshIcon, CheckCircle, Cancel } from '@mui/icons-material';
import { EstimationService } from '../services/EstimationService';
import { ProjectEstimation, EstimationStatus, NoteType } from '../types/estimation';
import SpecificationViewer from '../components/estimation/SpecificationViewer';
import ClarificationQA from '../components/estimation/ClarificationQA';

export const AdminEstimationPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [estimations, setEstimations] = useState<ProjectEstimation[]>([]);
  const [selectedEstimation, setSelectedEstimation] = useState<ProjectEstimation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Action dialogs
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [noteDialog, setNoteDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [regenerateDialog, setRegenerateDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const loadEstimations = async () => {
    try {
      setLoading(true);
      const response = await EstimationService.getAdminEstimations({
        status: filterStatus as any,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      setEstimations(response.data);
      
      if (id) {
        const estimation = response.data.find(e => e.id === parseInt(id));
        if (estimation) {
          loadEstimationDetails(parseInt(id));
        }
      }
    } catch (err: any) {
      setError('Failed to load estimations');
    } finally {
      setLoading(false);
    }
  };

  const loadEstimationDetails = async (estimationId: number) => {
    try {
      const response = await EstimationService.getAdminEstimation(estimationId);
      setSelectedEstimation(response.estimation as any);
    } catch (err) {
      console.error('Failed to load estimation details:', err);
    }
  };

  useEffect(() => {
    loadEstimations();
  }, [id, filterStatus]);

  const handleApprove = async () => {
    if (!selectedEstimation) return;
    try {
      await EstimationService.approveEstimation(selectedEstimation.id, { notes });
      setApproveDialog(false);
      setNotes('');
      loadEstimationDetails(selectedEstimation.id);
    } catch (err: any) {
      alert('Failed to approve estimation');
    }
  };

  const handleReject = async () => {
    if (!selectedEstimation) return;
    try {
      await EstimationService.rejectEstimation(selectedEstimation.id, {
        rejection_reason: rejectionReason,
        notes,
      });
      setRejectDialog(false);
      setRejectionReason('');
      setNotes('');
      loadEstimationDetails(selectedEstimation.id);
    } catch (err: any) {
      alert('Failed to reject estimation');
    }
  };

  const handleAddNote = async () => {
    if (!selectedEstimation) return;
    try {
      await EstimationService.addNote(selectedEstimation.id, {
        note_type: NoteType.GENERAL,
        content: notes,
        is_internal: true,
      });
      setNoteDialog(false);
      setNotes('');
      loadEstimationDetails(selectedEstimation.id);
    } catch (err: any) {
      alert('Failed to add note');
    }
  };

  const handleConvertToProject = async () => {
    if (!selectedEstimation) return;
    setConvertDialog(true);
  };

  const confirmConvertToProject = async () => {
    if (!selectedEstimation) return;
    setConvertDialog(false);
    try {
      await EstimationService.convertToProject(selectedEstimation.id);
      loadEstimationDetails(selectedEstimation.id);
    } catch (err: any) {
      alert('Failed to convert to project');
    }
  };

  const handleRegenerate = async () => {
    if (!selectedEstimation) return;
    setRegenerateDialog(true);
  };

  const confirmRegenerate = async () => {
    if (!selectedEstimation) return;
    setRegenerateDialog(false);
    try {
      await EstimationService.regenerateSpecification(selectedEstimation.id);
      loadEstimationDetails(selectedEstimation.id);
    } catch (err: any) {
      alert('Failed to regenerate specification');
    }
  };

  if (selectedEstimation) {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => setSelectedEstimation(null)} sx={{ mb: 2 }}>
          ← Back to List
        </Button>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box>
                <Typography variant="h4">{selectedEstimation.project_name}</Typography>
                <Typography color="text.secondary" paragraph>
                  Client: {selectedEstimation.client?.name || 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={selectedEstimation.status_label} color="primary" />
                  {selectedEstimation.estimated_cost_usd && (
                    <Chip
                      label={EstimationService.formatCost(selectedEstimation.estimated_cost_usd)}
                      color="success"
                    />
                  )}
                </Box>
              </Box>
              <Stack spacing={1}>
                {selectedEstimation.status === EstimationStatus.REVIEWED && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => setApproveDialog(true)}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setRejectDialog(true)}
                    >
                      Reject
                    </Button>
                  </>
                )}
                {selectedEstimation.status === EstimationStatus.APPROVED && (
                  <Button variant="contained" onClick={handleConvertToProject}>
                    Convert to Project
                  </Button>
                )}
                {selectedEstimation.status === EstimationStatus.SPECIFICATION_COMPLETE && (
                  <Button variant="outlined" onClick={handleRegenerate}>
                    Regenerate Spec
                  </Button>
                )}
                <Button variant="outlined" onClick={() => setNoteDialog(true)}>
                  Add Note
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Details" />
          <Tab label="Questions" />
          {selectedEstimation.latest_specification && <Tab label="Specification" />}
          <Tab label="Notes" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6">Project Details</Typography>
                <Typography paragraph>{selectedEstimation.project_description}</Typography>
              </CardContent>
            </Card>
          )}
          {activeTab === 1 && (
            <ClarificationQA
              estimationId={selectedEstimation.id}
              clarifications={selectedEstimation.clarifications || []}
              readonly
            />
          )}
          {activeTab === 2 && selectedEstimation.latest_specification && (
            <SpecificationViewer
              specification={selectedEstimation.latest_specification}
              allVersions={selectedEstimation.specifications}
              estimationId={selectedEstimation.id}
            />
          )}
          {activeTab === 3 && (
            <Card>
              <CardContent>
                <Typography variant="h6">Notes</Typography>
                {selectedEstimation.notes?.map((note) => (
                  <Box key={note.id} sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">{note.content}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {note.user?.name} • {EstimationService.formatDate(note.created_at)}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
          <DialogTitle>Approve Estimation</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Approval Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialog(false)}>Cancel</Button>
            <Button onClick={handleApprove} variant="contained" color="success">
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
          <DialogTitle>Reject Estimation</DialogTitle>
          <DialogContent>
            <TextField
              required
              fullWidth
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button
              onClick={handleReject}
              variant="contained"
              color="error"
              disabled={!rejectionReason.trim()}
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Note Dialog */}
        <Dialog open={noteDialog} onClose={() => setNoteDialog(false)}>
          <DialogTitle>Add Note</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Note Content"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote} variant="contained" disabled={!notes.trim()}>
              Add Note
            </Button>
          </DialogActions>
        </Dialog>

        {/* Convert to Project Confirmation Dialog */}
        <Dialog open={convertDialog} onClose={() => setConvertDialog(false)}>
          <DialogTitle>Convert to Project</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to convert this estimation to a project? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertDialog(false)}>Cancel</Button>
            <Button onClick={confirmConvertToProject} variant="contained" color="primary">
              Convert
            </Button>
          </DialogActions>
        </Dialog>

        {/* Regenerate Specification Confirmation Dialog */}
        <Dialog open={regenerateDialog} onClose={() => setRegenerateDialog(false)}>
          <DialogTitle>Regenerate Specification</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to regenerate the specification? This will create a new version and cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRegenerateDialog(false)}>Cancel</Button>
            <Button onClick={confirmRegenerate} variant="contained" color="primary">
              Regenerate
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Project Estimations</Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadEstimations}>
          Refresh
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={filterStatus}
            label="Filter by Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="clarification_needed">Clarification Needed</MenuItem>
            <MenuItem value="specification_complete">Specification Complete</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <Stack spacing={2}>
        {estimations.map((estimation) => (
          <Card
            key={estimation.id}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/admin/estimations/${estimation.id}`)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6">{estimation.project_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Client: {estimation.client?.name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Submitted: {EstimationService.formatDate(estimation.submitted_at)}
                  </Typography>
                </Box>
                <Box>
                  <Chip label={estimation.status_label} size="small" />
                  {estimation.estimated_cost_usd && (
                    <Typography variant="caption" display="block">
                      {EstimationService.formatCost(estimation.estimated_cost_usd)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default AdminEstimationPage;
